import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import config from 'constants/config'
import {
  formatCharterFareClassListForBanner,
  getOrderedCharterFareClasses,
} from 'lib/charterFlights/charterOfferFareClassRestrictions'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import { isWhitelabelBrand } from 'lib/whitelabels/whitelabels'

interface Props {
  offerId: string
}

function HotelSearchTileCharterFareClassBanner({ offerId }: Props) {
  const isLifestyleBrand = isWhitelabelBrand(config.BRAND)
  const intl = useIntl()
  const {
    charterBundle,
    allowedFareClassesByOfferId,
    hasFareClassRestrictionsByOfferId,
  } = useMaldivesDirectBundleContext()

  const allowedFareClasses = useMemo(() => {
    return allowedFareClassesByOfferId.get(offerId)
  }, [allowedFareClassesByOfferId, offerId])
  const hasFareClassRestrictions = useMemo(() => {
    return hasFareClassRestrictionsByOfferId.get(offerId) ?? false
  }, [hasFareClassRestrictionsByOfferId, offerId])

  const orderedAllowedFareClasses = useMemo(() => {
    if (!allowedFareClasses || allowedFareClasses.length === 0) {
      return undefined
    }
    return getOrderedCharterFareClasses(allowedFareClasses)
  }, [allowedFareClasses])

  const bannerMessage = useMemo(() => {
    if (!orderedAllowedFareClasses || orderedAllowedFareClasses.length === 0) {
      return undefined
    }
    const formattedClasses = formatCharterFareClassListForBanner(orderedAllowedFareClasses, intl)
    return intl.formatMessage(
      {
        id: 'WkVBX-yBskVtl07NlB_7',
        defaultMessage: 'Available exclusively with {formattedClasses} flights',
        description: 'Banner indicating which fare classes are available for a charter bundle hotel',
      },
      { formattedClasses },
    )
  }, [orderedAllowedFareClasses, intl])

  if (isLifestyleBrand || !charterBundle || !hasFareClassRestrictions || !allowedFareClasses || !orderedAllowedFareClasses || !bannerMessage) {
    return null
  }

  return (
    <MessageBanner kind="promotional-primary">
      <b>{bannerMessage}</b>
    </MessageBanner>
  )
}

export default HotelSearchTileCharterFareClassBanner
